import { EditorTab } from "./_EditorTab";
import { ProjectLoadingScreen } from "../ProjectLoadingScreen";
import { DiffViewTab } from "./_DiffViewTab";
import { MonacoMarkdownEditor } from "./_MonacoMarkdownEditor";

import { useParams } from "react-router-dom";
import { useEffect } from "react";

import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useDiffStore } from "@/zapi-frontend/stores/diff";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useMemoViewStore } from "@/zapi-frontend/stores/memoView";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { hasPendingDiffs } from "@/zapi-frontend/utils/diff-utils";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { useProjectGenerationState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";

export function MemoIsland() {
  const { activeChapter } = useChapterStore();
  const { isLabs, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { isRawView, isFullWidth, setRawView } = useMemoViewStore();

  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const chapterId = activeChapter?.id?.toString() || "";

  const { isFetchProjectLoading } = useProjectStore();
  // Chapter content stays read-only until the whole analysis finishes — an edit made
  // mid-run would be overwritten by the generator (INV-2286).
  const { isSetupActive } = useProjectGenerationState(projectId);

  const { getDiffEditor } = useDiffStore();
  const htmlContent = getDiffEditor(projectId, chapterId)?.htmlContent;
  const hasDiff = !!htmlContent && hasPendingDiffs(htmlContent);

  useEffect(() => {
    if (!isLabs && isRawView) {
      setRawView(false);
    }
  }, [isLabs, isRawView, setRawView]);

  return (
    <div className={cn("flex w-full h-full mx-auto", isFullWidth ? "max-w-none" : "max-w-[720px]")}>
      <div className="w-full flex flex-col bg-card h-full px-4 laptop:px-0">
        {isFetchProjectLoading && (
          <div className="flex h-full items-center justify-center">
            <ProjectLoadingScreen />
          </div>
        )}

        {!isFetchProjectLoading && (
          <div className="p-0 m-0 flex-1 flex flex-col relative" data-testid="memo-editor-container">
            {/* TipTap Editor - Always rendered, visibility controlled by CSS */}
            <div className={isRawView ? "hidden" : "block h-full"}>
              <EditorTab
                projectId={projectId}
                isLoading={isFetchProjectLoading}
                isEditable={hasEdit && !isSetupActive}
                className={!hasDiff ? "swap-anim" : "swap-hidden"}
              />
              <DiffViewTab className={hasDiff ? "swap-anim" : "swap-hidden"} />
            </div>

            {/* Monaco Editor - Always rendered, visibility controlled by CSS */}
            <div className={isRawView ? "block h-full" : "hidden"}>
              <div className="p-1 pt-0 flex-1 flex flex-col h-full">
                {isFetchProjectLoading ? (
                  <div className="flex items-center justify-center h-48 text-[14px] text-gray-500">
                    <div className="h-4 w-4 border-t-2 border-blue-500 border-solid rounded-full animate-spin mr-2"></div>
                    Loading memo...
                  </div>
                ) : (
                  <MonacoMarkdownEditor projectId={projectId} className="flex-1" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
